import type { TranscriptItem } from 'types'

import { DEFAULT_UI_LOCALE, type UiLocale } from '../src/core/contracts.js'
import { buildCanonicalDiscoveryReportData } from '../src/core/pdf/canonical-report-data.js'
import { generateDiscoveryReportHTML } from '../src/core/pdf/templates/discovery-report-template.js'
import type { DiscoveryReportData } from '../src/core/pdf/utils/discovery-report-types.js'
import type { CanonicalSessionProjectionV1 } from '../src/core/session/session-projection.js'
import { tReport } from '../src/i18n/report-labels.js'

export interface CanonicalDiscoveryReportInput {
  projection: CanonicalSessionProjectionV1
  evidenceIds: readonly string[]
}

/**
 * Canonical report data has exactly one source: an accepted server projection
 * and the evidence IDs frozen by its report.requested event.
 */
export function buildDiscoveryReportFromProjection(
  input: CanonicalDiscoveryReportInput,
): DiscoveryReportData {
  return buildCanonicalDiscoveryReportData(input.projection, input.evidenceIds)
}

/** Report capability audit derived from canonical evidence and turn manifests. */
export function buildReportEvidenceAudit(
  projection: CanonicalSessionProjectionV1,
  evidenceIds: readonly string[] = projection.evidence
    .filter(evidence => evidence.status === 'verified')
    .map(evidence => evidence.evidenceId),
) {
  const acceptedIds = new Set(evidenceIds)
  const evidence = projection.evidence.filter(
    item => item.status === 'verified' && acceptedIds.has(item.evidenceId),
  )
  const manifests = Object.values(projection.turnCapabilities)
  const capabilities = new Set(manifests.flatMap(manifest => manifest.capabilities))

  return {
    hasVoice:
      projection.transcript.some(
        entry => entry.modality === 'voice' && entry.state !== 'interim',
      ) || capabilities.has('voice'),
    hasScreen:
      evidence.some(item => item.kind === 'screen') || capabilities.has('screen_share'),
    hasWebcam:
      evidence.some(item => item.kind === 'webcam') || capabilities.has('webcam'),
    hasFiles:
      evidence.some(item => item.kind === 'file') || capabilities.has('file_analysis'),
    hasResearch:
      evidence.some(item => item.kind === 'research') ||
      capabilities.has('grounded_research'),
    hasTools: capabilities.has('tool_call'),
    hasLocation: capabilities.has('location_weather'),
    toolSuccessCount: manifests.reduce(
      (count, manifest) => count + manifest.successfulToolNames.length,
      0,
    ),
    toolFailureCount: manifests.reduce(
      (count, manifest) => count + manifest.failedToolNames.length,
      0,
    ),
    visualObservationCount: evidence.filter(
      item => item.kind === 'screen' || item.kind === 'webcam',
    ).length,
    groundedSearchCount: evidence.filter(item => item.kind === 'research').length,
    voiceObservationCount: projection.transcript.filter(
      entry => entry.modality === 'voice' && entry.state !== 'interim',
    ).length,
  }
}

/** Render already-canonical report data for the browser preview. */
export function generateDiscoveryReportHTMLClient(
  data: DiscoveryReportData,
  options?: { locale?: UiLocale; darkMode?: boolean },
): string {
  const htmlOptions: { locale: UiLocale; darkMode?: boolean } = {
    locale: options?.locale ?? DEFAULT_UI_LOCALE,
  }
  if (options?.darkMode !== undefined) {
    htmlOptions.darkMode = options.darkMode
  }
  return generateDiscoveryReportHTML(data, htmlOptions)
}

export const DISCOVERY_REPORT_ACK_TEXT =
  "I've prepared your AI Transformation Proposal — strategy, opportunity map, roadmap, and investment estimate. Review it below and book a call to finalize scope."

/** Visitor-facing prose that promised a PDF/preview before prepare succeeded. */
export const DISCOVERY_REPORT_PROMISE_RE =
  /preview will appear|generating your|i(?:'m| am) (?:preparing|generating).{0,80}(?:pdf|brief|proposal|report)/i

export function looksLikeDiscoveryReportPromise(text: string | undefined): boolean {
  return Boolean(text && DISCOVERY_REPORT_PROMISE_RE.test(text))
}

/** Create a derived UI attachment after the server commits the report artifact. */
export function createDiscoveryReportTranscriptItem(
  data: DiscoveryReportData,
  htmlContent: string,
  pdfDataUrl?: string,
  locale: UiLocale = DEFAULT_UI_LOCALE,
): TranscriptItem {
  const company = data.client.company || data.client.name || 'Session'
  const attachment: TranscriptItem['attachment'] = {
    type: 'discovery_report',
    name: tReport('pageTitle', locale, { company }),
    htmlContent,
    url: data.bookingUrl,
  }
  if (pdfDataUrl) attachment.data = pdfDataUrl

  return {
    id: `insights-report-${data.reportRef}`,
    role: 'model',
    text: DISCOVERY_REPORT_ACK_TEXT,
    timestamp: new Date(),
    isFinal: true,
    status: 'complete',
    attachment,
  }
}
